import { useCallback, useMemo, useState, type CSSProperties } from "react";
import { useApp, useUi, useUiDispatch } from "@/app/AppContext";
import { DETAILS_LAYOUT } from "@/app/uiState";
import { useResizablePanel } from "@/components/useResizablePanel";
import { canCreateSystemAnalysis, isSuperadmin } from "@/domain/permissions";
import {
  ALL_LOCATIONS_ID,
  getCriterionStatusCountsByLocation,
  getDisplayStatusCounts,
  SYSTEM_STATUS_DISPLAY_ORDER,
  type SystemCriterion,
} from "@/domain/systemAnalysis";
import { LocalFileLoader } from "@/features/toolbar/LocalFileLoader";
import { format } from "@/i18n/translator";
import { useI18n } from "@/i18n/useI18n";
import { SystemAnalysisDialog } from "./SystemAnalysisDialog";
import { SystemAnalysisOverTime } from "./SystemAnalysisOverTime";
import { SystemDetailPanel } from "./SystemDetailPanel";
import { SystemGrid } from "./SystemGrid";
import { useSystemOverTime } from "./useLocationAnalyses";
import { pick, statusLabelKey, useCurrentSystemAnalysis } from "./useSystemAnalysis";

const LEGEND_NOTE_KEYS = {
  fulfilled: "systemLegendFulfilled",
  almost_fulfilled: "systemLegendAlmostFulfilled",
  partially_fulfilled: "systemLegendPartiallyFulfilled",
  only_limited: "systemLegendOnlyLimited",
  not_fulfilled: "systemLegendNotFulfilled",
  insufficient_information: "systemLegendInsufficientInformation",
  zero: "systemLegendZero",
} as const;

export function SystemTab() {
  const { t, language, locale } = useI18n();
  const { authMode, user, data } = useApp();
  const ui = useUi();
  const dispatch = useUiDispatch();
  const { analysis, locations, location, domains } = useCurrentSystemAnalysis();
  const [dialogOpen, setDialogOpen] = useState(false);
  /** Year of a new analysis opened from the "over time" first-analysis banner. */
  const [planYear, setPlanYear] = useState<number | null>(null);
  const locationId = location?.locationId ?? ALL_LOCATIONS_ID;
  const isAll = locationId === ALL_LOCATIONS_ID;
  const overTime = useSystemOverTime();
  const overTimeLocationName =
    pick(language, overTime.location.locationName.no, overTime.location.locationName.en) ||
    overTime.location.locationId;

  const counts = useMemo(
    () => getDisplayStatusCounts(analysis, locationId, domains),
    [analysis, locationId, domains],
  );
  const locationCounts = useMemo(
    () => (isAll ? getCriterionStatusCountsByLocation(analysis) : null),
    [isAll, analysis],
  );
  const shownStatuses = SYSTEM_STATUS_DISPLAY_ORDER.filter(
    (status) => status !== "unknown" && counts[status] > 0,
  ) as (keyof typeof LEGEND_NOTE_KEYS)[];

  const selectedDomain = domains.find((domain) =>
    domain.items.some((item) => item.id === ui.system.criterionId),
  );
  const selectedItem = selectedDomain?.items.find((item) => item.id === ui.system.criterionId);
  const detailVisible = !!selectedItem;
  const isRight = ui.detailsPosition === "right";
  const commitWidth = useCallback(
    (width: number) => dispatch({ type: "setDetailsWidth", width }),
    [dispatch],
  );
  const { containerRef, panelWidth, splitterProps } = useResizablePanel({
    width: ui.detailsWidth,
    minPanelWidth: DETAILS_LAYOUT.minDetailsWidth,
    minMainWidth: DETAILS_LAYOUT.minSystemMainWidth,
    enabled: isRight && detailVisible,
    onCommit: commitWidth,
  });
  const useRightLayout = isRight && detailVisible && window.innerWidth > 980;
  const layoutStyle: CSSProperties = {
    gridTemplateColumns: useRightLayout
      ? `minmax(0, 1fr) var(--splitter-track-width, 12px) minmax(420px, ${Math.round(panelWidth)}px)`
      : "minmax(0, 1fr)",
  };

  const audit = analysis?.auditByLocation?.[locationId];
  const auditDate = audit?.updatedAt ? new Date(audit.updatedAt) : null;
  const canManage = authMode === "individual" && canCreateSystemAnalysis(user);
  // "Over time" edits the newest analysis of the chosen location.
  const latestYear = overTime.history.years.at(-1);
  const editAnalysis = overTime.active
    ? latestYear === undefined
      ? null
      : (overTime.history.analysisByYear[latestYear] ?? null)
    : analysis;
  const editLocationId = overTime.active ? overTime.location.locationId : locationId;
  const hasExisting =
    !!editAnalysis &&
    editLocationId !== ALL_LOCATIONS_ID &&
    editAnalysis.locations.some((entry) => entry.locationId === editLocationId);
  const aboutTitle =
    pick(language, analysis?.aboutTitle?.no, analysis?.aboutTitle?.en).trim() ||
    t("systemAboutTitle");
  const aboutText =
    pick(language, analysis?.aboutText?.no, analysis?.aboutText?.en).trim() || t("systemAboutText");

  const dialogLocations = useMemo(() => {
    if (user?.role === "localadmin") return user.location ? [user.location] : [];
    // Locations with interventions or an analysis in any year, so a location
    // can get a new year even when the selected year has no analysis for it.
    const names = new Set(data.dataset.regions.filter(Boolean));
    for (const entry of data.systemData.analyses.flatMap((item) => item.locations)) {
      if (entry.locationId !== ALL_LOCATIONS_ID) names.add(entry.locationId);
    }
    return [...names].sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base", numeric: true }),
    );
  }, [user, data.dataset.regions, data.systemData]);
  const criteria = useMemo<SystemCriterion[]>(() => {
    const source = editAnalysis?.domainsByLocation[ALL_LOCATIONS_ID] ?? domains;
    return [
      ...new Map(source.flatMap((domain) => domain.items).map((item) => [item.id, item])).values(),
    ];
  }, [editAnalysis, domains]);

  const openDialog = (year: number | null = null) => {
    if (!criteria.length) return window.alert(t("systemCreateAnalysisNoCriteria"));
    if (!dialogLocations.length) return window.alert(t("systemCreateAnalysisError"));
    setPlanYear(year);
    setDialogOpen(true);
  };
  const editingExisting = hasExisting && planYear === null;
  const initialDialogLocation = editingExisting
    ? editLocationId
    : user?.role === "localadmin"
      ? (user.location ?? "")
      : dialogLocations.includes(editLocationId)
        ? editLocationId
        : (dialogLocations[0] ?? "");

  return (
    <>
      <div className="toolbar">
        <div id="system-toolbar-row" className="toolbar-row">
          {overTime.available && (
            <div className="system-segmented" role="group" aria-label={t("systemViewLabel")}>
              {(["single", "overTime"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  className={(mode === "overTime") === overTime.active ? "active" : undefined}
                  aria-pressed={(mode === "overTime") === overTime.active}
                  onClick={() => dispatch({ type: "setSystemMode", mode })}
                >
                  {t(mode === "single" ? "systemViewSingle" : "systemViewOverTime")}
                </button>
              ))}
            </div>
          )}
          {overTime.active ? (
            <div className="system-period">
              <span id="label-system-period">{t("systemPeriod")}</span>
              {overTime.period ? (
                <div
                  className="system-segmented"
                  role="group"
                  aria-labelledby="label-system-period"
                >
                  {overTime.periods.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={option === overTime.period ? "active" : undefined}
                      aria-pressed={option === overTime.period}
                      onClick={() => dispatch({ type: "setSystemPeriod", period: option.id })}
                    >
                      {t(option.id === "all" ? "systemPeriodAll" : "systemPeriodLastTwo")}
                    </button>
                  ))}
                </div>
              ) : (
                latestYear !== undefined && (
                  <span className="system-period-pill">
                    {format(t("systemFirstAnalysisPill"), { year: latestYear })}
                  </span>
                )
              )}
            </div>
          ) : (
            <div>
              <label htmlFor="system-analysis-select">
                <span id="label-system-analysis">{t("systemAnalysis")}</span>:
              </label>
              <select
                id="system-analysis-select"
                value={analysis?.analysisId ?? ""}
                onChange={(event) =>
                  dispatch({ type: "selectSystemAnalysis", analysisId: event.target.value })
                }
              >
                {data.systemData.analyses.map((entry) => (
                  <option key={entry.analysisId} value={entry.analysisId}>
                    {pick(language, entry.analysisName.no, entry.analysisName.en) ||
                      entry.analysisId}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="system-location-select">
              <span id="label-system-location">{t("systemLocation")}</span>:
            </label>
            <select
              id="system-location-select"
              value={overTime.active ? overTime.location.locationId : locationId}
              onChange={(event) =>
                dispatch({ type: "selectSystemLocation", locationId: event.target.value })
              }
            >
              {(overTime.active ? overTime.locations : locations).map((entry) => (
                <option key={entry.locationId} value={entry.locationId}>
                  {pick(language, entry.locationName.no, entry.locationName.en) || entry.locationId}
                </option>
              ))}
            </select>
          </div>
          {(authMode === "shared" || isSuperadmin(user)) && <LocalFileLoader compact />}
          <div className="system-toolbar-actions">
            {canManage && (
              <button
                type="button"
                className="system-analysis-create-btn"
                onClick={() => openDialog()}
              >
                {hasExisting ? t("systemEditAnalysis") : t("systemCreateAnalysis")}
              </button>
            )}
            <button
              type="button"
              className="system-about-btn"
              onClick={() => dispatch({ type: "toggleSystemAbout" })}
            >
              <span className="about-info-icon" title="About">
                i
              </span>{" "}
              {t("systemAbout")}
            </button>
          </div>
        </div>
        {data.systemError && (
          <p id="system-analysis-data-error" className="system-analysis-data-error" role="alert">
            {`${t("systemAnalysisLoadError")} (${data.systemError.message})`}
          </p>
        )}
        {auditDate && !overTime.active && (
          <div id="system-analysis-audit" aria-live="polite">
            {`${t("systemAnalysisLastChanged")}: ${audit?.updatedBy || t("ikkeAngitt")} · ${
              Number.isNaN(auditDate.getTime())
                ? audit?.updatedAt
                : new Intl.DateTimeFormat(locale, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }).format(auditDate)
            }`}
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        id="system-layout"
        className={`system-layout ${useRightLayout ? "details-layout-right" : "details-layout-below"}${detailVisible ? "" : " details-panel-hidden"}`}
        style={layoutStyle}
      >
        <div className="system-main" id="system-main">
          {ui.system.showAbout && (
            <div id="system-about-panel" className="system-about-panel">
              <strong>{aboutTitle}</strong> {aboutText}
            </div>
          )}
          {overTime.active ? (
            <SystemAnalysisOverTime
              history={overTime.history}
              period={overTime.period}
              locationName={overTimeLocationName}
              onPlan={canManage ? (year) => openDialog(year) : undefined}
            />
          ) : (
            <>
              <div id="system-summary" className="system-summary">
                {shownStatuses.map((status) => (
                  <span key={status} className="system-summary-item">
                    <span
                      className={`system-status-dot system-status-${status}`}
                      aria-hidden="true"
                    />
                    <span>
                      <strong>{counts[status]}</strong> {t(statusLabelKey(status))}
                    </span>
                  </span>
                ))}
              </div>
              <SystemGrid domains={domains} locationCounts={locationCounts} />
              <div id="system-legend" className="system-legend">
                <strong>{t("systemSummaryLabel")}</strong>
                <div className="system-legend-grid">
                  {shownStatuses.map((status) => (
                    <div key={status} className="system-legend-item">
                      <div className="system-legend-title">
                        <span className={`system-status-dot system-status-${status}`} />
                        {t(statusLabelKey(status))}
                      </div>
                      <div className="system-legend-note">{t(LEGEND_NOTE_KEYS[status])}</div>
                    </div>
                  ))}
                </div>
                <div className="system-legend-divider-note">
                  <div className="system-legend-note">{t("systemAggregateRule")}</div>
                </div>
              </div>
            </>
          )}
        </div>
        <div
          {...splitterProps}
          id="system-splitter"
          style={{ display: useRightLayout ? "block" : "none" }}
          aria-label="Resize system details panel"
        />
        {selectedItem && selectedDomain && (
          <aside id="system-info-box" className="info-box system-info-box">
            <SystemDetailPanel
              analysis={analysis}
              domain={selectedDomain}
              item={selectedItem}
              isAllLocations={isAll}
            />
          </aside>
        )}
      </div>

      {dialogOpen && (
        <SystemAnalysisDialog
          analysis={planYear === null ? editAnalysis : null}
          criteria={criteria}
          locations={dialogLocations}
          initialLocation={initialDialogLocation}
          editing={editingExisting}
          initialYear={planYear === null ? undefined : String(planYear)}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}
